/** @format */

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import {
	ProductStylePrices,
	ProductsData,
	ReadyForNextRoundAddOnCard
} from '@shared/data-access/interfaces';
import { forkJoin, of } from 'rxjs';

import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { ProductService } from '@shared/data-access/services';
import { catchError } from 'rxjs/operators';

@Component({
	selector: 'pdp-ready-for-next-round-add-ons',
	standalone: true,
	imports: [CommonModule, HmfButtonComponent],
	templateUrl: './ready-for-next-round-add-ons.component.html',
	styleUrl: './ready-for-next-round-add-ons.component.scss'
})
export class ReadyForNextRoundAddOnsComponent implements OnInit {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly productService = inject(ProductService);

	cards: ReadyForNextRoundAddOnCard[] = [];
	UNAVAILABLE_IMAGE_URL =
		'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';

	ngOnInit(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		// Override in CEOPS if needed
		const ecodes = window?.readyForNextRoundEcodes ?? [
			'24MAXUMXFLTRXWHTDGBL',
			'24MAXMMXFLTRGLVXXGLV'
		];

		const productCalls$ = ecodes.map((ecode) =>
			this.productService
				.getProductByEcodeV4$(ecode, false, 1)
				.pipe(catchError(() => of(null)))
		);

		forkJoin(productCalls$).subscribe((allProductsData: (ProductsData | null)[]) => {
			this.cards = this.mapProductsToCards(allProductsData);
		});
	}

	private mapProductsToCards(
		productsData: (ProductsData | null)[]
	): ReadyForNextRoundAddOnCard[] {
		return productsData
			.filter((product) => product?.skus?.length) // ignore product if call failed (null) or oos (no skus)
			.map((product: ProductsData) => ({
				title: product.style.name,
				ecode: product.style.partNumber,
				imageUrl: ProductService.generateImageUrlFromAttributes(
					product,
					'&hei=250&wid=250'
				),
				prices: product.style.prices,
				isClearancePrice: this.isClearancePrice(product.style.prices),
				isSeePriceInCart: this.isSeePriceInCart(product.style.prices),
				hasListPriceRange: this.hasListPriceRange(product.style.prices),
				hasOfferPriceRange: this.hasOfferPriceRange(product.style.prices)
			}));
	}

	private isClearancePrice(prices?: ProductStylePrices) {
		return Boolean(
			Number(prices?.minlistprice) &&
				(Number(prices?.minlistprice) > Number(prices?.minofferprice) ||
					Number(prices?.maxlistprice) > Number(prices?.maxofferprice))
		);
	}

	private isSeePriceInCart(prices?: ProductStylePrices) {
		return Boolean(
			(!Number(prices?.minofferprice) && !Number(prices?.maxofferprice)) ||
				prices?.mapPriceIndicator === 1
		);
	}

	private hasListPriceRange(prices?: ProductStylePrices) {
		return Boolean(Number(prices?.maxlistprice) > Number(prices?.minlistprice));
	}

	private hasOfferPriceRange(prices?: ProductStylePrices) {
		return Boolean(Number(prices?.maxofferprice) > Number(prices?.minofferprice));
	}

	protected openQuickViewPanel(ecode: string) {
		window.quickview?.openProductQuickView(ecode, 'ReadyForNextRoundAddOns', {
			additionalData: ''
		});
	}
}
